import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchArticle, fetchArticles, deleteArticle, resolveImageUrl, fetchContributors, fetchCategories } from "../services/api";
import { useAuth } from "./AuthContext";
import { Clock, ArrowLeft, Share2, Bookmark, Twitter, Facebook, Pencil, Trash2, Loader2, Camera, CalendarClock } from "lucide-react";
import ArticleCard from "./ArticleCard";

// Category info resolved from API
const getCategoryInfo = (categoryId, cats) => {
  return (cats || []).find((c) => c.id === categoryId);
};

/**
 * Process article content to ensure proper paragraph formatting.
 * Handles: raw text, text with \n, HTML with single <p> wrapping, etc.
 */
const processContent = (content) => {
  if (!content) return "";

  // Normalize line endings
  let text = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // If content already has multiple <p> tags, it's properly formatted
  const pCount = (text.match(/<p>/gi) || []).length;
  if (pCount > 1) return text;

  // Strip single wrapping <p></p> if present
  let inner = text;
  if (inner.startsWith("<p>") && pCount === 1) {
    inner = inner.replace(/^<p>/i, "").replace(/<\/p>$/i, "");
  }

  // If there are no HTML tags at all, or just single <p> wrapper content
  // Split on double newlines first, then single newlines
  let paragraphs;
  if (inner.includes("\n\n")) {
    paragraphs = inner.split("\n\n");
  } else if (inner.includes("\n")) {
    paragraphs = inner.split("\n");
  } else {
    return `<p>${inner}</p>`;
  }

  return paragraphs
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .map((p) => {
      // Don't double-wrap if already has block-level tags
      if (p.startsWith("<h") || p.startsWith("<p>") || p.startsWith("<blockquote") || p.startsWith("<ul") || p.startsWith("<ol")) {
        return p;
      }
      return `<p>${p}</p>`;
    })
    .join("\n\n");
};

const ArticlePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contributorBio, setContributorBio] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const loadArticle = async () => {
      setLoading(true);
      try {
        const [data, cats] = await Promise.all([fetchArticle(id), fetchCategories()]);
        setArticle(data);
        setCategories(cats);

        // Load contributor bio
        try {
          const contributors = await fetchContributors();
          const match = contributors.find((c) => c.name === data.author?.name);
          if (match && match.bio) setContributorBio(match.bio);
        } catch {}

        // Load related
        const related = await fetchArticles({ category: data.category, limit: 5 });
        setRelatedArticles(
          (related.articles || []).filter((a) => a.id !== data.id).slice(0, 4)
        );
      } catch (err) {
        console.error("Failed to load article:", err);
      }
      setLoading(false);
    };
    loadArticle();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    setDeleting(true);
    try {
      await deleteArticle(id);
      navigate("/");
    } catch (err) {
      alert("Failed to delete article");
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#1B7A3D]" size={32} />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Article not found</h1>
        <Link to="/" className="text-[#1B7A3D] font-medium hover:underline">
          Back to Home
        </Link>
      </div>
    );
  }

  const cat = getCategoryInfo(article.category, categories);
  const formattedDate = new Date(article.date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-[#1B7A3D] transition-colors flex items-center gap-1">
              <ArrowLeft size={14} />
              Home
            </Link>
            <span className="text-gray-300">/</span>
            <Link
              to={`/category/${article.category}`}
              className="hover:text-[#1B7A3D] transition-colors font-medium"
              style={{ color: cat?.color }}
            >
              {cat?.name}
            </Link>
          </div>
          {/* Edit / Delete */}
          {user && (
          <div className="flex items-center gap-2">
            <Link
              to={`/edit/${article.id}`}
              data-testid="edit-article-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-[#1B7A3D] hover:bg-green-50 rounded-lg transition-all"
            >
              <Pencil size={14} />
              Edit
            </Link>
            <button
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-article-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            >
              {deleting ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
              Delete
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Scheduled Banner */}
        {article.status === "scheduled" && article.scheduled_at && (
          <div data-testid="scheduled-banner" className="flex items-center gap-2 mb-6 px-4 py-3 bg-indigo-50 border border-indigo-100 rounded-xl">
            <CalendarClock size={16} className="text-indigo-500" />
            <span className="text-sm font-semibold text-indigo-700">
              Scheduled for {new Date(article.scheduled_at).toLocaleString("en-AU", {
                timeZone: "Australia/Sydney",
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })} AEST
            </span>
          </div>
        )}
        <div className="mb-6">
          <span
            className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full text-white mb-4"
            style={{ backgroundColor: cat?.color || "#1B7A3D" }}
          >
            {cat?.name}
          </span>
          <h1 data-testid="article-title" className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 leading-tight mb-4">
            {article.title}
          </h1>
          <p data-testid="article-excerpt" className="text-lg text-gray-500 leading-relaxed mb-6">
            {article.excerpt}
          </p>

          {/* Author & Meta */}
          <div className="flex items-center justify-between flex-wrap gap-4 pb-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img
                src={article.author?.avatar}
                alt={article.author?.name}
                className="w-12 h-12 rounded-full border-2 border-gray-100"
              />
              <div>
                <span className="text-sm font-bold text-gray-900 block">
                  {article.author?.name}
                </span>
                <span className="text-xs text-gray-500">
                  {article.author?.role} · {formattedDate}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-sm text-gray-500 mr-4">
                <Clock size={15} />
                {article.readTime}
              </div>
              <button
                data-testid="share-btn"
                onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) {
                    navigator.share({ title: article.title, text: article.excerpt, url });
                  } else {
                    navigator.clipboard.writeText(url).then(() => alert("Link copied to clipboard!"));
                  }
                }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#1B7A3D] transition-all"
              >
                <Share2 size={18} />
              </button>
              <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#1B7A3D] transition-all">
                <Bookmark size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Hero Image */}
        {article.image && (
          <div className="rounded-2xl overflow-hidden mb-2">
            <img
              src={resolveImageUrl(article.image)}
              alt={article.title}
              className="w-full aspect-[2/1] object-cover"
              style={{ objectPosition: `${article.image_focal_x ?? 50}% ${article.image_focal_y ?? 50}%` }}
            />
          </div>
        )}

        {/* Photo Credit */}
        {article.image_credit && (
          <div className="flex items-center gap-1.5 mb-8 px-1">
            <Camera size={13} className="text-gray-400" />
            <span className="text-xs text-gray-400">
              Photo by{" "}
              {article.image_credit_url ? (
                <a
                  href={article.image_credit_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-[#1B7A3D] underline decoration-dotted underline-offset-2 transition-colors"
                >
                  {article.image_credit}
                </a>
              ) : (
                <span className="text-gray-500 font-medium">{article.image_credit}</span>
              )}
            </span>
          </div>
        )}

        {/* Article Content */}
        <div
          data-testid="article-content"
          className="article-content prose prose-lg max-w-none
            prose-headings:font-black prose-headings:text-gray-900
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-a:text-[#1B7A3D] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900
            mb-12"
          dangerouslySetInnerHTML={{ __html: processContent(article.content) }}
        />

        {/* Share section */}
        <div className="flex items-center gap-4 py-6 border-t border-b border-gray-200 mb-12">
          <span className="text-sm font-semibold text-gray-700">Share this article:</span>
          <a
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-twitter-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1DA1F2]/10 text-[#1DA1F2] hover:bg-[#1DA1F2]/20 transition-colors text-sm font-medium"
          >
            <Twitter size={16} />
            Twitter
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-facebook-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20 transition-colors text-sm font-medium"
          >
            <Facebook size={16} />
            Facebook
          </a>
        </div>

        {/* Author Bio */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-12">
          <div className="flex items-start gap-4">
            <img
              src={article.author?.avatar}
              alt={article.author?.name}
              className="w-16 h-16 rounded-full"
            />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-gray-900">{article.author?.name}</h4>
                  <p className="text-sm text-[#1B7A3D] font-medium mb-2">{article.author?.role}</p>
                </div>
                {user && (
                  <Link
                    to="/contributors"
                    data-testid="edit-author-bio-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-[#1B7A3D] hover:bg-green-50 rounded-lg transition-all"
                    title="Edit contributor profile"
                  >
                    <Pencil size={14} />
                    Edit Profile
                  </Link>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {contributorBio || ""}
              </p>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-1 h-6 bg-[#1B7A3D] rounded-full" />
              <h2 className="text-xl font-bold text-gray-900">More in {cat?.name}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default ArticlePage;
